// ============================================================================
// AchievementsContext — orquestra avaliação e expõe notificações reativas
// ============================================================================
// Roda evaluateAllClosedMonths quando dados de performance/professionals mudam,
// e mantém o estado das notificações em React para os componentes consumirem.
// ============================================================================

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useData } from './DataContext.jsx';
import { useAuth } from './AuthContext.jsx';
import { usePerformance } from './PerformanceContext.jsx';
import { cloudSave } from '../utils/supabase.js';
import {
  evaluateAllClosedMonths,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  ensureSchemaUpToDate,
} from '../utils/achievementsTracking.js';

const AchievementsContext = createContext(null);

export function AchievementsProvider({ children }) {
  const { professionals, cloudLoaded } = useData();
  const { performance } = usePerformance();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(() => getNotifications());
  const [tick, setTick] = useState(0);
  const [schemaChecked, setSchemaChecked] = useState(false);

  // Detecta schema antigo APÓS o cloud terminar de carregar.
  // Limpa local + cloud — evita que loadFromCloud "ressuscite" dados velhos.
  useEffect(() => {
    if (!cloudLoaded || schemaChecked) return;
    (async () => {
      const result = await ensureSchemaUpToDate({ cloudSave });
      if (result.reset) setNotifications([]);
      setSchemaChecked(true);
    })();
  }, [cloudLoaded, schemaChecked]);

  // Avalia meses (passados + corrente) sempre que performance muda
  // — só roda DEPOIS do schemaChecked pra evitar processar com dados velhos.
  useEffect(() => {
    if (!schemaChecked) return;
    if (!professionals?.length || !performance?.length) return;
    const newNotifs = evaluateAllClosedMonths(professionals, performance);
    if (newNotifs.length > 0) {
      setNotifications(getNotifications());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaChecked, professionals?.length, performance?.length, performance, tick]);

  // Notificações do usuário logado (se for prof)
  const myProfId = user?.professionalId;
  const myNotifications = useMemo(() => {
    if (!myProfId) return [];
    return notifications
      .filter(n => n.profId === myProfId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [notifications, myProfId]);

  const myUnreadCount = useMemo(
    () => myNotifications.filter(n => !n.readAt).length,
    [myNotifications]
  );

  const markAsRead = useCallback((id) => {
    markNotificationRead(id);
    setNotifications(getNotifications());
  }, []);

  const markAllAsRead = useCallback(() => {
    if (!myProfId) return;
    markAllNotificationsRead(myProfId);
    setNotifications(getNotifications());
  }, [myProfId]);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
    setNotifications(getNotifications());
  }, []);

  return (
    <AchievementsContext.Provider value={{
      notifications: myNotifications,
      unreadCount: myUnreadCount,
      markAsRead,
      markAllAsRead,
      refresh,
    }}>
      {children}
    </AchievementsContext.Provider>
  );
}

export function useAchievements() {
  const ctx = useContext(AchievementsContext);
  if (!ctx) throw new Error('useAchievements deve ser usado dentro de <AchievementsProvider>');
  return ctx;
}
